import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectKategorien,
  createKategorie,
  updateKategorie,
  deleteKategorie,
} from 'modules/todo/features/todoSlice';
import { FARB_PALETTE } from './todoConstants';

/** Panel zum Verwalten der Kategorien (anlegen, umbenennen, umfärben, löschen). */
export default function KategorienPanel() {
  const dispatch = useDispatch();
  const kategorien = useSelector(selectKategorien);

  const [name, setName] = useState('');
  const [farbe, setFarbe] = useState(FARB_PALETTE[8]);

  const anlegen = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    dispatch(createKategorie({ name: name.trim(), farbe }));
    setName('');
  };

  return (
    <div className="space-y-6">
      <form onSubmit={anlegen} className="rounded-xl border border-line bg-paper-soft p-4">
        <p className="mb-3 font-mono text-xs uppercase tracking-wide text-ink-muted">Neue Kategorie</p>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name der Kategorie…"
            className="flex-1 rounded-lg border border-line bg-paper px-3 py-2 text-sm text-ink outline-none focus:border-brand-500"
          />
          <FarbAuswahl farbe={farbe} onChange={setFarbe} />
          <button type="submit" className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-500">
            Anlegen
          </button>
        </div>
      </form>

      {kategorien.length === 0 ? (
        <p className="rounded-xl border border-dashed border-line bg-paper-soft p-8 text-center text-ink-muted">
          Noch keine Kategorien angelegt.
        </p>
      ) : (
        <div className="space-y-2">
          {kategorien.map((k) => (
            <KategorieZeile
              key={k.uuid}
              kategorie={k}
              onUpdate={(daten) => dispatch(updateKategorie({ uuid: k.uuid, kategorie: daten }))}
              onDelete={() => {
                if (window.confirm(`Kategorie „${k.name}" löschen? Zugeordnete Aufgaben bleiben erhalten.`)) {
                  dispatch(deleteKategorie(k.uuid));
                }
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Eine Kategorie-Zeile mit umschaltbarem Bearbeiten-Modus. */
function KategorieZeile({ kategorie, onUpdate, onDelete }) {
  const [bearbeiten, setBearbeiten] = useState(false);
  const [name, setName] = useState(kategorie.name);
  const [farbe, setFarbe] = useState(kategorie.farbe);

  const speichern = () => {
    if (!name.trim()) return;
    onUpdate({ name: name.trim(), farbe });
    setBearbeiten(false);
  };

  if (bearbeiten) {
    return (
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-line bg-paper-soft p-3">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="flex-1 rounded-lg border border-line bg-paper px-3 py-1.5 text-sm text-ink outline-none focus:border-brand-500"
        />
        <FarbAuswahl farbe={farbe} onChange={setFarbe} />
        <button type="button" onClick={speichern} className="rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-500">
          OK
        </button>
        <button type="button" onClick={() => setBearbeiten(false)} className="rounded-lg px-3 py-1.5 text-sm text-ink-muted hover:bg-ink/5">
          Abbrechen
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 rounded-xl border border-line bg-paper-soft p-3">
      <span className="h-4 w-4 rounded-full" style={{ backgroundColor: kategorie.farbe }} />
      <span className="flex-1 text-ink">{kategorie.name}</span>
      <button type="button" onClick={() => setBearbeiten(true)} className="rounded-lg px-2 py-1 text-ink-muted hover:bg-ink/5 hover:text-ink" aria-label="Bearbeiten">
        ✎
      </button>
      <button type="button" onClick={onDelete} className="rounded-lg px-2 py-1 text-ink-muted hover:bg-loss/10 hover:text-loss" aria-label="Löschen">
        🗑
      </button>
    </div>
  );
}

/** Farbwähler als Reihe klickbarer Farbpunkte. */
function FarbAuswahl({ farbe, onChange }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {FARB_PALETTE.map((f) => (
        <button
          key={f}
          type="button"
          onClick={() => onChange(f)}
          className={`h-6 w-6 rounded-full transition-transform hover:scale-110 ${farbe === f ? 'ring-2 ring-ink ring-offset-1 ring-offset-paper-soft' : ''}`}
          style={{ backgroundColor: f }}
          aria-label={`Farbe ${f}`}
        />
      ))}
    </div>
  );
}
